import { createContext, useContext } from 'react';

import PropTypes from 'prop-types';

import { useActionScope } from '../hooks/useActionScope';
import { useJobNotifications } from '../hooks/useJobNotifications';
import { useJobStatus } from '../hooks/useJobStatus';
import { useLeadActions } from '../hooks/useLeadActions';
import { useAddLeadsToEnrichmentQueue, useDeleteLeads } from '../models/companies/mutations';
import { useAbortSearchJob } from '../models/jobs/mutations';
import { useLeadUIStore } from '../stores/leadUIStore';

import { useLeadData } from './LeadDataContext';

const LeadJobContext = createContext(null);

/**
 * Hook to access lead job context
 * Provides: job progress, enrichment, job actions
 */
export const useLeadJobs = () => {
	const context = useContext(LeadJobContext);
	if (!context) {
		throw new Error('useLeadJobs must be used within LeadJobProvider');
	}
	return context;
};

/**
 * Provider for lead job management
 * Handles: Job progress, enrichment, job notifications, job actions
 */
export const LeadJobProvider = ({ children }) => {
	// Get data from LeadDataContext
	const { selectedSearchQueryId, searchQueries } = useLeadData();
	const actionScope = useActionScope();

	// Get UI action from store directly
	const setDetailLeadId = useLeadUIStore((s) => s.setDetailLeadId);

	// Get job status using shared hook (React Query caches this)
	const {
		runningJobs,
		hasRunningCompanyScraperJob,
		hasCancellingCompanyScraperJob,
		hasRunningAnalysisJob,
		hasRunningEnrichmentJob,
		hasCancellingEnrichmentJob,
		runningJobsByType,
	} = useJobStatus(selectedSearchQueryId);

	// Mutations
	const addLeadsToEnrichmentMutation = useAddLeadsToEnrichmentQueue();
	const deleteLeadsMutation = useDeleteLeads();
	const abortSearchJobMutation = useAbortSearchJob();

	// Lead actions hook
	const {
		handleStartEnrichment: handleEnrichmentAction,
		handleDeleteLeads,
		handleExtendCompanySearch,
		handleAbortSearchJob,
	} = useLeadActions({
		addLeadsToEnrichmentMutation,
		deleteLeadsMutation,
		abortSearchJobMutation,
		setDetailLeadId,
	});

	useJobNotifications(runningJobs);

	const handleStartEnrichment = (options = {}) => handleEnrichmentAction(actionScope, options);

	// Wrapper for extend company search to pass additional context
	const handleExtendCompanySearch_Wrapper = async (additionalResults, searchParameters = null) => {
		return await handleExtendCompanySearch(
			selectedSearchQueryId,
			searchQueries,
			additionalResults,
			searchParameters,
		);
	};

	const contextValue = {
		// Job progress state
		runningJobs,
		runningJobsByType,
		hasRunningAnalysisJob,
		hasRunningCompanyScraperJob,
		hasCancellingCompanyScraperJob,
		hasRunningEnrichmentJob,
		hasCancellingEnrichmentJob,

		// Actions
		handleStartEnrichment,
		handleDeleteLeads,
		handleExtendCompanySearch: handleExtendCompanySearch_Wrapper,
		handleAbortSearchJob,
	};

	return <LeadJobContext.Provider value={contextValue}>{children}</LeadJobContext.Provider>;
};

LeadJobProvider.propTypes = {
	children: PropTypes.node.isRequired,
};
